import { avatarUrlsFromWire, type AvatarUrls } from "./avatarDisplay";

export const JWT_STORAGE_KEY = "styx_jwt";

export type ApiUploadProgress = {
  phase: "uploading" | "processing";
  loaded: number;
  total: number | null;
};

export function getStoredJwt(): string | null {
  try {
    return localStorage.getItem(JWT_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function setStoredJwt(token: string): void {
  localStorage.setItem(JWT_STORAGE_KEY, token);
}

export function clearStoredJwt(): void {
  try {
    localStorage.removeItem(JWT_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export type RegisterResponse = {
  user_id: string;
  username: string;
  token: string;
};

export type LoginResponse = {
  user_id: string;
  username: string;
  public_key_ed: string;
  public_key_x: string;
  encrypted_keys: string;
  key_salt: string;
  token: string;
  /** Present when server supports per-account theme sync. */
  theme?: unknown;
};

export async function apiRegister(body: {
  email: string;
  password: string;
  username: string;
  user_id: string;
  public_key_ed: string;
  public_key_x: string;
  encrypted_keys: string;
  key_salt: string;
  invite_code: string;
}): Promise<RegisterResponse> {
  const res = await fetch("/api/register", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = (await res.json()) as RegisterResponse & { error?: string };
  if (!res.ok) {
    const err = data.error;
    if (err === "REGISTRATION_CLOSED") {
      throw new Error("Регистрация закрыта (на сервере не заданы STYX_INVITE_CODES / STYX_INVITE_FILE).");
    }
    if (err === "INVALID_INVITE") {
      throw new Error("Неверный инвайт-код.");
    }
    throw new Error(err ?? `ошибка регистрации (${res.status})`);
  }
  return data;
}

export type ServerMessageRow = {
  id: string;
  from_id: string;
  to_id: string;
  nonce: string;
  ciphertext: string;
  sender_nonce: string | null;
  sender_ciphertext: string | null;
  created_at: number;
  delivered: boolean;
  read: boolean;
  deleted?: boolean;
};

export type SyncEventRow = {
  seq: number;
  event: "message_created" | "message_delivered" | "message_read" | "message_deleted";
  message_id: string;
  created_at: number;
};

/** GET /api/messages/:peerId — ciphertext history (JWT). Only incoming messages are decryptable locally. */
export async function apiFetchMessageHistory(
  peerId: string,
  limit = 100,
  beforeMs?: number,
  beforeId?: string,
): Promise<{ peer_id: string; messages: ServerMessageRow[] }> {
  const token = getStoredJwt();
  if (!token) throw new Error("нет токена");
  const params = new URLSearchParams();
  params.set("limit", String(limit));
  if (beforeMs != null) params.set("before", String(beforeMs));
  if (beforeId != null && beforeId !== "") params.set("before_id", beforeId);
  const res = await fetch(`/api/messages/${encodeURIComponent(peerId)}?${params}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = (await res.json()) as {
    peer_id?: string;
    messages?: ServerMessageRow[];
    error?: string;
  };
  if (!res.ok) {
    throw new Error(data.error ?? `ошибка истории (${res.status})`);
  }
  return { peer_id: data.peer_id ?? peerId, messages: data.messages ?? [] };
}

/** GET /api/messages/:peerId?ids=uuid,uuid — догрузка конкретных сообщений диалога (цитаты / reply). */
export async function apiFetchMessagesByIds(
  peerId: string,
  ids: string[],
): Promise<{ peer_id: string; messages: ServerMessageRow[] }> {
  if (ids.length === 0) return { peer_id: peerId, messages: [] };
  const token = getStoredJwt();
  if (!token) throw new Error("нет токена");
  const params = new URLSearchParams();
  params.set("ids", ids.join(","));
  const res = await fetch(`/api/messages/${encodeURIComponent(peerId)}?${params}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = (await res.json()) as {
    peer_id?: string;
    messages?: ServerMessageRow[];
    error?: string;
  };
  if (!res.ok) {
    throw new Error(data.error ?? `ошибка сообщений (${res.status})`);
  }
  return { peer_id: data.peer_id ?? peerId, messages: data.messages ?? [] };
}

export async function apiFetchSync(
  afterSeq: number,
  limit = 100,
): Promise<{ events: SyncEventRow[]; messages: Record<string, ServerMessageRow> }> {
  const token = getStoredJwt();
  if (!token) throw new Error("нет токена");
  const params = new URLSearchParams();
  params.set("after", String(Math.max(0, Math.floor(afterSeq))));
  params.set("limit", String(limit));
  const res = await fetch(`/api/sync?${params}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = (await res.json()) as {
    events?: SyncEventRow[];
    messages?: Record<string, ServerMessageRow>;
    error?: string;
  };
  if (!res.ok) {
    throw new Error(data.error ?? `ошибка синхронизации (${res.status})`);
  }
  return { events: data.events ?? [], messages: data.messages ?? {} };
}

export type WorkspaceListItem = {
  id: string;
  name: string;
  role: string;
  joined_at: number;
  created_at: number;
  /** Сумма непрочитанных по всем темам (как в списке topics). */
  unread_count: number;
};

export type WorkspaceTopicItem = {
  id: string;
  name: string;
  created_at: number;
  unread_count: number;
  last_message_at: number | null;
  last_message_preview: string | null;
  last_message_author_id: string | null;
};

export type WorkspaceMemberItem = {
  user_id: string;
  username: string;
  avatar_url?: string | null;
  avatar?: AvatarUrls | null;
  role: "owner" | "admin" | "member";
  joined_at: number;
};

/** Aggregated reaction on a workspace message (HTTP). */
export type WorkspaceTopicReactionWire = {
  emoji: string;
  count: number;
  own: boolean;
};

/** One workspace topic message row from GET/POST (plain text body). */
export type WorkspaceTopicServerMessage = {
  id: string;
  author_id: string;
  body: string;
  reply_to?: string | null;
  created_at: number;
  reactions?: WorkspaceTopicReactionWire[];
};

export type WorkspaceSearchMessage = WorkspaceTopicServerMessage & {
  topic_id: string;
  topic_name: string;
  rank: number;
};

/** GET /api/workspaces — workspaces the current user belongs to (JWT). */
export async function apiFetchWorkspaces(): Promise<{ workspaces: WorkspaceListItem[] }> {
  const token = getStoredJwt();
  if (!token) throw new Error("нет токена");
  const res = await fetch("/api/workspaces", {
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = (await res.json()) as {
    workspaces?: WorkspaceListItem[];
    error?: string;
  };
  if (!res.ok) {
    throw new Error(data.error ?? `workspaces (${res.status})`);
  }
  const list = data.workspaces ?? [];
  return {
    workspaces: list.map((w) => ({
      ...w,
      unread_count: typeof w.unread_count === "number" ? w.unread_count : 0,
    })),
  };
}

export type ApiFetchWorkspaceTopicsOptions = {
  limit?: number;
  cursor?: string;
  ensureTopicId?: string | null;
};

/** GET /api/workspaces/:id/topics — topics if member (JWT). Without `limit`, full list; with `limit`, paginated (`has_more`, `next_cursor`). */
export async function apiFetchWorkspaceTopics(
  workspaceId: string,
  options?: ApiFetchWorkspaceTopicsOptions,
): Promise<{
  workspace_id: string;
  topics: WorkspaceTopicItem[];
  pinned_topic_ids?: string[];
  has_more?: boolean;
  next_cursor?: string | null;
}> {
  const token = getStoredJwt();
  if (!token) throw new Error("нет токена");
  const params = new URLSearchParams();
  if (options?.limit != null) params.set("limit", String(options.limit));
  if (options?.cursor) params.set("cursor", options.cursor);
  if (options?.ensureTopicId) params.set("ensure_topic_id", options.ensureTopicId);
  const q = params.toString();
  const res = await fetch(
    `/api/workspaces/${encodeURIComponent(workspaceId)}/topics${q ? `?${q}` : ""}`,
    {
      headers: { Authorization: `Bearer ${token}` },
    },
  );
  const data = (await res.json()) as {
    workspace_id?: string;
    topics?: WorkspaceTopicItem[];
    has_more?: boolean;
    next_cursor?: string | null;
    error?: string;
  };
  if (!res.ok) {
    throw new Error(data.error ?? `topics (${res.status})`);
  }
  const pinnedRaw = (data as { pinned_topic_ids?: string[] }).pinned_topic_ids;
  const pinned_topic_ids = Array.isArray(pinnedRaw) ? pinnedRaw : undefined;
  return {
    workspace_id: data.workspace_id ?? workspaceId,
    topics: (data.topics ?? []).map(normalizeWorkspaceTopicItem),
    ...(pinned_topic_ids != null ? { pinned_topic_ids } : {}),
    ...(options?.limit != null
      ? {
          has_more: Boolean(data.has_more),
          next_cursor: typeof data.next_cursor === "string" ? data.next_cursor : null,
        }
      : {}),
  };
}

function normalizeWorkspaceTopicItem(t: Partial<WorkspaceTopicItem> & Pick<WorkspaceTopicItem, "id" | "name" | "created_at">): WorkspaceTopicItem {
  return {
    id: t.id,
    name: t.name,
    created_at: t.created_at,
    unread_count: typeof t.unread_count === "number" ? t.unread_count : 0,
    last_message_at: typeof t.last_message_at === "number" ? t.last_message_at : null,
    last_message_preview: typeof t.last_message_preview === "string" ? t.last_message_preview : null,
    last_message_author_id: typeof t.last_message_author_id === "string" ? t.last_message_author_id : null,
  };
}

/** POST …/topics/:topicId/read — mark topic read through latest message (JWT). */
export async function apiMarkWorkspaceTopicRead(workspaceId: string, topicId: string): Promise<void> {
  const token = getStoredJwt();
  if (!token) throw new Error("нет токена");
  const res = await fetch(
    `/api/workspaces/${encodeURIComponent(workspaceId)}/topics/${encodeURIComponent(topicId)}/read`,
    { method: "POST", headers: { Authorization: `Bearer ${token}` } },
  );
  if (!res.ok) {
    let msg = `mark read (${res.status})`;
    try {
      const errBody = (await res.json()) as { error?: string };
      if (errBody.error) msg = errBody.error;
    } catch {
      /* 204 or empty */
    }
    throw new Error(msg);
  }
}

/** GET …/topics/:topicId/messages — topic history if workspace member (JWT). */
export async function apiFetchWorkspaceTopicMessages(
  workspaceId: string,
  topicId: string,
  limit = 100,
  beforeMs?: number,
  beforeId?: string,
): Promise<{ workspace_id: string; topic_id: string; messages: WorkspaceTopicServerMessage[] }> {
  const token = getStoredJwt();
  if (!token) throw new Error("нет токена");
  const params = new URLSearchParams();
  params.set("limit", String(limit));
  if (beforeMs != null) params.set("before", String(beforeMs));
  if (beforeId != null && beforeId !== "") params.set("before_id", beforeId);
  const res = await fetch(
    `/api/workspaces/${encodeURIComponent(workspaceId)}/topics/${encodeURIComponent(topicId)}/messages?${params}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  const data = (await res.json()) as {
    workspace_id?: string;
    topic_id?: string;
    messages?: WorkspaceTopicServerMessage[];
    error?: string;
  };
  if (!res.ok) {
    throw new Error(data.error ?? `workspace topic messages (${res.status})`);
  }
  return {
    workspace_id: data.workspace_id ?? workspaceId,
    topic_id: data.topic_id ?? topicId,
    messages: data.messages ?? [],
  };
}

/** POST …/topics/:topicId/messages — create message (JWT). */
export async function apiPostWorkspaceTopicMessage(
  workspaceId: string,
  topicId: string,
  body: string,
  replyTo?: string | null,
): Promise<{ message: WorkspaceTopicServerMessage }> {
  const token = getStoredJwt();
  if (!token) throw new Error("нет токена");
  const res = await fetch(
    `/api/workspaces/${encodeURIComponent(workspaceId)}/topics/${encodeURIComponent(topicId)}/messages`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        body,
        ...(replyTo != null && replyTo !== "" ? { reply_to: replyTo } : {}),
      }),
    },
  );
  const data = (await res.json()) as {
    message?: WorkspaceTopicServerMessage | string;
    error?: string;
  };
  if (!res.ok) {
    const detail = typeof data.message === "string" ? data.message : undefined;
    throw new Error(detail ?? data.error ?? `workspace topic send (${res.status})`);
  }
  if (!data.message || typeof data.message !== "object") {
    throw new Error("некорректный ответ сервера");
  }
  return { message: data.message as WorkspaceTopicServerMessage };
}

/** GET /api/workspaces/:id/messages/search — full-text search over workspace messages (JWT). */
export async function apiSearchWorkspaceMessages(
  workspaceId: string,
  query: string,
  options?: {
    topicId?: string;
    limit?: number;
    beforeMs?: number;
    beforeId?: string;
  },
): Promise<{
  workspace_id: string;
  query: string;
  messages: WorkspaceSearchMessage[];
  has_more: boolean;
  next_before: number | null;
  next_before_id: string | null;
}> {
  const token = getStoredJwt();
  if (!token) throw new Error("нет токена");
  const params = new URLSearchParams();
  params.set("q", query);
  if (options?.topicId) params.set("topic_id", options.topicId);
  if (options?.limit != null) params.set("limit", String(options.limit));
  if (options?.beforeMs != null) params.set("before", String(options.beforeMs));
  if (options?.beforeId) params.set("before_id", options.beforeId);
  const res = await fetch(
    `/api/workspaces/${encodeURIComponent(workspaceId)}/messages/search?${params.toString()}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  const data = (await res.json()) as {
    workspace_id?: string;
    query?: string;
    messages?: WorkspaceSearchMessage[];
    has_more?: boolean;
    next_before?: number | null;
    next_before_id?: string | null;
    error?: string;
  };
  if (!res.ok) {
    throw new Error(data.error ?? `workspace search (${res.status})`);
  }
  return {
    workspace_id: data.workspace_id ?? workspaceId,
    query: data.query ?? query,
    messages: data.messages ?? [],
    has_more: Boolean(data.has_more),
    next_before: typeof data.next_before === "number" ? data.next_before : null,
    next_before_id: typeof data.next_before_id === "string" ? data.next_before_id : null,
  };
}

/** POST /api/workspaces — create a new workspace (caller becomes owner). */
export async function apiCreateWorkspace(
  name: string,
): Promise<{ workspace: WorkspaceListItem }> {
  const token = getStoredJwt();
  if (!token) throw new Error("нет токена");
  const res = await fetch("/api/workspaces", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ name }),
  });
  const data = (await res.json()) as { workspace?: WorkspaceListItem; error?: string; message?: string };
  if (!res.ok) {
    throw new Error(data.message ?? data.error ?? `workspace create (${res.status})`);
  }
  if (!data.workspace) throw new Error("некорректный ответ сервера");
  const w = data.workspace;
  return {
    workspace: {
      ...w,
      unread_count: typeof w.unread_count === "number" ? w.unread_count : 0,
    },
  };
}

/** POST /api/workspaces/:id/topics — create topic (owner/admin only). */
export async function apiCreateWorkspaceTopic(
  workspaceId: string,
  name: string,
): Promise<{ workspace_id: string; topic: WorkspaceTopicItem }> {
  const token = getStoredJwt();
  if (!token) throw new Error("нет токена");
  const res = await fetch(
    `/api/workspaces/${encodeURIComponent(workspaceId)}/topics`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ name }),
    },
  );
  const data = (await res.json()) as {
    workspace_id?: string;
    topic?: WorkspaceTopicItem;
    error?: string;
    message?: string;
  };
  if (!res.ok) {
    throw new Error(data.message ?? data.error ?? `topic create (${res.status})`);
  }
  if (!data.topic) throw new Error("некорректный ответ сервера");
  return {
    workspace_id: data.workspace_id ?? workspaceId,
    topic: normalizeWorkspaceTopicItem(data.topic),
  };
}

/** GET /api/workspaces/:id/members — list members if workspace member (JWT). */
export async function apiFetchWorkspaceMembers(
  workspaceId: string,
): Promise<{ workspace_id: string; members: WorkspaceMemberItem[] }> {
  const token = getStoredJwt();
  if (!token) throw new Error("нет токена");
  const res = await fetch(`/api/workspaces/${encodeURIComponent(workspaceId)}/members`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = (await res.json()) as {
    workspace_id?: string;
    members?: WorkspaceMemberItem[];
    error?: string;
  };
  if (!res.ok) {
    throw new Error(data.error ?? `workspace members (${res.status})`);
  }
  return {
    workspace_id: data.workspace_id ?? workspaceId,
    members: data.members ?? [],
  };
}

/** POST /api/workspaces/:id/members — add by username (owner/admin; admin only as member). */
export async function apiAddWorkspaceMember(
  workspaceId: string,
  username: string,
  role?: "member" | "admin",
): Promise<{ member: WorkspaceMemberItem }> {
  const token = getStoredJwt();
  if (!token) throw new Error("нет токена");
  const res = await fetch(`/api/workspaces/${encodeURIComponent(workspaceId)}/members`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(role ? { username, role } : { username }),
  });
  const data = (await res.json()) as { member?: WorkspaceMemberItem; error?: string; message?: string };
  if (!res.ok) {
    throw new Error(data.message ?? data.error ?? `add member (${res.status})`);
  }
  if (!data.member) throw new Error("некорректный ответ сервера");
  return { member: data.member };
}

export async function apiUpdateWorkspaceMemberRole(
  workspaceId: string,
  userId: string,
  role: "member" | "admin",
): Promise<{ member: WorkspaceMemberItem }> {
  const token = getStoredJwt();
  if (!token) throw new Error("нет токена");
  const res = await fetch(
    `/api/workspaces/${encodeURIComponent(workspaceId)}/members/${encodeURIComponent(userId)}`,
    {
      method: "PATCH",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ role }),
    },
  );
  const data = (await res.json()) as { member?: WorkspaceMemberItem; error?: string; message?: string };
  if (!res.ok) {
    throw new Error(data.message ?? data.error ?? `update member role (${res.status})`);
  }
  if (!data.member) throw new Error("некорректный ответ сервера");
  return { member: data.member };
}

export async function apiRemoveWorkspaceMember(workspaceId: string, userId: string): Promise<void> {
  const token = getStoredJwt();
  if (!token) throw new Error("нет токена");
  const res = await fetch(
    `/api/workspaces/${encodeURIComponent(workspaceId)}/members/${encodeURIComponent(userId)}`,
    {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    },
  );
  if (res.status === 204) return;
  const data = (await res.json().catch(() => ({}))) as { error?: string };
  if (!res.ok) {
    throw new Error(data.error ?? `remove member (${res.status})`);
  }
}

/** DELETE /api/contacts/:contactId — remove mutual contact (JWT). */
export async function apiRemoveContact(contactId: string): Promise<void> {
  const token = getStoredJwt();
  if (!token) throw new Error("нет токена");
  const res = await fetch(`/api/contacts/${encodeURIComponent(contactId)}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = (await res.json().catch(() => ({}))) as { error?: string };
  if (!res.ok) {
    throw new Error(data.error ?? `remove contact (${res.status})`);
  }
}

/** DELETE /api/workspaces/:id — delete workspace (owner only). */
export async function apiDeleteWorkspace(workspaceId: string): Promise<void> {
  const token = getStoredJwt();
  if (!token) throw new Error("нет токена");
  const res = await fetch(`/api/workspaces/${encodeURIComponent(workspaceId)}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = (await res.json().catch(() => ({}))) as { error?: string };
  if (!res.ok) {
    throw new Error(data.error ?? `delete workspace (${res.status})`);
  }
}

/** DELETE /api/workspaces/:wid/topics/:tid — delete topic (owner/admin; not "general"; not last). */
export async function apiDeleteWorkspaceTopic(workspaceId: string, topicId: string): Promise<void> {
  const token = getStoredJwt();
  if (!token) throw new Error("нет токена");
  const res = await fetch(
    `/api/workspaces/${encodeURIComponent(workspaceId)}/topics/${encodeURIComponent(topicId)}`,
    {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    },
  );
  const data = (await res.json().catch(() => ({}))) as { error?: string };
  if (!res.ok) {
    throw new Error(data.error ?? `delete topic (${res.status})`);
  }
}

/** PATCH /api/workspaces/:id — rename workspace (owner/admin). */
export async function apiRenameWorkspace(workspaceId: string, name: string): Promise<{ name: string }> {
  const token = getStoredJwt();
  if (!token) throw new Error("нет токена");
  const res = await fetch(`/api/workspaces/${encodeURIComponent(workspaceId)}`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ name }),
  });
  const data = (await res.json().catch(() => ({}))) as { name?: string; error?: string; message?: string };
  if (!res.ok) {
    throw new Error(data.message ?? data.error ?? `rename workspace (${res.status})`);
  }
  if (typeof data.name !== "string") throw new Error("некорректный ответ сервера");
  return { name: data.name };
}

/** PATCH /api/workspaces/:wid/topics/:tid — rename topic (owner/admin; not "general"). */
export async function apiRenameWorkspaceTopic(
  workspaceId: string,
  topicId: string,
  name: string,
): Promise<{ topic: { id: string; name: string } }> {
  const token = getStoredJwt();
  if (!token) throw new Error("нет токена");
  const res = await fetch(
    `/api/workspaces/${encodeURIComponent(workspaceId)}/topics/${encodeURIComponent(topicId)}`,
    {
      method: "PATCH",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ name }),
    },
  );
  const data = (await res.json().catch(() => ({}))) as {
    topic?: { id?: string; name?: string };
    error?: string;
    message?: string;
  };
  if (!res.ok) {
    throw new Error(data.message ?? data.error ?? `rename topic (${res.status})`);
  }
  const t = data.topic;
  if (!t || typeof t.id !== "string" || typeof t.name !== "string") {
    throw new Error("некорректный ответ сервера");
  }
  return { topic: { id: t.id, name: t.name } };
}

function normalizeUploadBytes(body: ArrayBuffer | Uint8Array): Uint8Array {
  const src = body instanceof Uint8Array ? body : new Uint8Array(body);
  const copy = new Uint8Array(src.byteLength);
  copy.set(src);
  return copy;
}

async function apiUploadBinary(
  url: string,
  token: string,
  body: ArrayBuffer | Uint8Array,
  fallbackError: string,
  onProgress?: (progress: ApiUploadProgress) => void,
): Promise<{ blob_id: string }> {
  const bytes = normalizeUploadBytes(body);
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", url);
    xhr.responseType = "text";
    xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    xhr.setRequestHeader("Content-Type", "application/octet-stream");
    if (onProgress) {
      onProgress({ phase: "uploading", loaded: 0, total: bytes.byteLength || null });
      xhr.upload.onprogress = (event) => {
        onProgress({
          phase: "uploading",
          loaded: event.loaded,
          total: event.lengthComputable ? event.total : bytes.byteLength || null,
        });
      };
      xhr.upload.onload = () => {
        onProgress({
          phase: "processing",
          loaded: bytes.byteLength,
          total: bytes.byteLength || null,
        });
      };
    }
    xhr.onerror = () => reject(new Error("ошибка сети при загрузке"));
    xhr.onabort = () => reject(new Error("загрузка отменена"));
    xhr.onload = () => {
      let data: { blob_id?: string; error?: string } = {};
      try {
        data = xhr.responseText ? (JSON.parse(xhr.responseText) as { blob_id?: string; error?: string }) : {};
      } catch {
        data = {};
      }
      if (xhr.status < 200 || xhr.status >= 300) {
        reject(new Error(data.error ?? `${fallbackError} (${xhr.status})`));
        return;
      }
      if (typeof data.blob_id !== "string") {
        reject(new Error("некорректный ответ загрузки"));
        return;
      }
      onProgress?.({ phase: "processing", loaded: bytes.byteLength, total: bytes.byteLength || null });
      resolve({ blob_id: data.blob_id });
    };
    xhr.send(new Blob([bytes.buffer as ArrayBuffer], { type: "application/octet-stream" }));
  });
}

/** POST encrypted image blob; returns server-side opaque id. */
export async function apiUploadEncryptedBlob(
  peerId: string,
  body: ArrayBuffer | Uint8Array,
  onProgress?: (progress: ApiUploadProgress) => void,
): Promise<{ blob_id: string }> {
  const token = getStoredJwt();
  if (!token) throw new Error("нет токена");
  return apiUploadBinary(
    `/api/uploads?peer_id=${encodeURIComponent(peerId)}`,
    token,
    body,
    "ошибка загрузки",
    onProgress,
  );
}

/** POST …/topics/:topicId/blobs — workspace image bytes (JPEG/PNG); membership + topic ACL. */
export async function apiUploadWorkspaceTopicBlob(
  workspaceId: string,
  topicId: string,
  body: ArrayBuffer | Uint8Array,
  onProgress?: (progress: ApiUploadProgress) => void,
): Promise<{ blob_id: string }> {
  const token = getStoredJwt();
  if (!token) throw new Error("нет токена");
  return apiUploadBinary(
    `/api/workspaces/${encodeURIComponent(workspaceId)}/topics/${encodeURIComponent(topicId)}/blobs`,
    token,
    body,
    "загрузка в workspace",
    onProgress,
  );
}

/** GET …/blobs/:blobId — workspace member; raw image bytes. */
export async function apiFetchWorkspaceBlob(workspaceId: string, blobId: string): Promise<ArrayBuffer> {
  const token = getStoredJwt();
  if (!token) throw new Error("нет токена");
  const res = await fetch(
    `/api/workspaces/${encodeURIComponent(workspaceId)}/blobs/${encodeURIComponent(blobId)}`,
    {
      headers: { Authorization: `Bearer ${token}` },
    },
  );
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error ?? `workspace blob (${res.status})`);
  }
  return res.arrayBuffer();
}

export type LinkPreviewResponse = {
  ok: boolean;
  /** OG parse failed; title is hostname-only fallback. */
  fallback?: boolean;
  pageUrl: string;
  title: string | null;
  description: string | null;
  siteName: string | null;
  imageProxyUrl: string | null;
};

/** GET /api/link-preview?url= — Open Graph summary (JWT). */
export async function apiFetchLinkPreview(pageUrl: string): Promise<LinkPreviewResponse> {
  const token = getStoredJwt();
  if (!token) throw new Error("нет токена");
  const params = new URLSearchParams();
  params.set("url", pageUrl);
  const res = await fetch(`/api/link-preview?${params}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = (await res.json()) as LinkPreviewResponse & { error?: string };
  if (!res.ok) {
    throw new Error(data.error ?? `превью (${res.status})`);
  }
  return data;
}

/** GET /api/link-preview/image?url= — image bytes for preview thumbnail (JWT). */
export async function apiFetchLinkPreviewImageBlob(proxyPathAndQuery: string): Promise<Blob> {
  const token = getStoredJwt();
  if (!token) throw new Error("нет токена");
  const res = await fetch(proxyPathAndQuery, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error ?? `превью-изображение (${res.status})`);
  }
  return res.blob();
}

export async function apiFetchEncryptedBlob(blobId: string): Promise<ArrayBuffer> {
  const token = getStoredJwt();
  if (!token) throw new Error("нет токена");
  const res = await fetch(`/api/uploads/${encodeURIComponent(blobId)}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error ?? `ошибка скачивания (${res.status})`);
  }
  return res.arrayBuffer();
}

/** PATCH /api/username — authenticated rename (JWT). */
export async function apiRenameUsername(username: string): Promise<{ username: string }> {
  const token = getStoredJwt();
  if (!token) throw new Error("нет токена");
  const res = await fetch("/api/username", {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ username }),
  });
  const data = (await res.json()) as { username?: string; error?: string };
  if (!res.ok) {
    throw new Error(data.error ?? `смена имени (${res.status})`);
  }
  if (typeof data.username !== "string") {
    throw new Error("некорректный ответ");
  }
  return { username: data.username };
}

export async function apiUploadAvatar(blob: Blob): Promise<{ avatar_url: string | null; avatar: AvatarUrls | null }> {
  const token = getStoredJwt();
  if (!token) throw new Error("нет токена");
  const mime = blob.type || "application/octet-stream";
  const res = await fetch("/api/avatar", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": mime,
    },
    body: blob,
  });
  const data = (await res.json()) as {
    avatar_url?: string | null;
    avatar?: { original_url?: string | null; terminal_url?: string | null } | null;
    error?: string;
  };
  if (!res.ok) throw new Error(data.error ?? `ошибка загрузки аватара (${res.status})`);
  const avatar: AvatarUrls | null =
    data.avatar && typeof data.avatar === "object"
      ? {
          original_url: typeof data.avatar.original_url === "string" ? data.avatar.original_url : null,
          terminal_url: typeof data.avatar.terminal_url === "string" ? data.avatar.terminal_url : null,
        }
      : avatarUrlsFromWire(undefined, data.avatar_url);
  return { avatar_url: data.avatar_url ?? null, avatar };
}

export async function apiDeleteAvatar(): Promise<void> {
  const token = getStoredJwt();
  if (!token) throw new Error("нет токена");
  const res = await fetch("/api/avatar", {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const data = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(data.error ?? `ошибка удаления аватара (${res.status})`);
  }
}

export type VaultWire = { id: string; name: string; created_at: number };

export type VaultServerMessageRow = {
  id: string;
  nonce: string;
  ciphertext: string;
  created_at: number;
  deleted: boolean;
};

export async function apiVaultPinStatus(): Promise<{ has_pin: boolean }> {
  const token = getStoredJwt();
  if (!token) throw new Error("нет токена");
  const res = await fetch("/api/vault-pin", { headers: { Authorization: `Bearer ${token}` } });
  const data = (await res.json()) as { has_pin?: boolean; error?: string };
  if (!res.ok) throw new Error(data.error ?? `vault-pin (${res.status})`);
  return { has_pin: Boolean(data.has_pin) };
}

export async function apiVaultPinSet(pin: string): Promise<void> {
  const token = getStoredJwt();
  if (!token) throw new Error("нет токена");
  const res = await fetch("/api/vault-pin", {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify({ pin }),
  });
  const data = (await res.json().catch(() => ({}))) as { error?: string };
  if (!res.ok) throw new Error(data.error ?? `vault-pin set (${res.status})`);
}

export async function apiVaultPinVerify(pin: string): Promise<boolean> {
  const token = getStoredJwt();
  if (!token) throw new Error("нет токена");
  const res = await fetch("/api/vault-pin/verify", {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify({ pin }),
  });
  const data = (await res.json()) as { valid?: boolean; error?: string };
  if (!res.ok) throw new Error(data.error ?? `vault-pin verify (${res.status})`);
  return Boolean(data.valid);
}

export async function apiVaultPinChange(oldPin: string, newPin: string): Promise<void> {
  const token = getStoredJwt();
  if (!token) throw new Error("нет токена");
  const res = await fetch("/api/vault-pin", {
    method: "PUT",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify({ old_pin: oldPin, new_pin: newPin }),
  });
  const data = (await res.json().catch(() => ({}))) as { error?: string };
  if (!res.ok) throw new Error(data.error ?? `vault-pin change (${res.status})`);
}

export async function apiFetchVaults(): Promise<VaultWire[]> {
  const token = getStoredJwt();
  if (!token) throw new Error("нет токена");
  const res = await fetch("/api/vaults", { headers: { Authorization: `Bearer ${token}` } });
  const data = (await res.json()) as VaultWire[] | { error?: string };
  if (!res.ok) throw new Error((data as { error?: string }).error ?? `vaults (${res.status})`);
  return Array.isArray(data) ? data : [];
}

export async function apiCreateVault(name: string): Promise<VaultWire> {
  const token = getStoredJwt();
  if (!token) throw new Error("нет токена");
  const res = await fetch("/api/vaults", {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify({ name }),
  });
  const data = (await res.json()) as VaultWire & { error?: string };
  if (!res.ok) throw new Error(data.error ?? `vault create (${res.status})`);
  return data;
}

export async function apiRenameVault(vaultId: string, name: string): Promise<void> {
  const token = getStoredJwt();
  if (!token) throw new Error("нет токена");
  const res = await fetch(`/api/vaults/${encodeURIComponent(vaultId)}`, {
    method: "PATCH",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify({ name }),
  });
  const data = (await res.json().catch(() => ({}))) as { error?: string };
  if (!res.ok) throw new Error(data.error ?? `vault rename (${res.status})`);
}

export async function apiDeleteVault(vaultId: string): Promise<void> {
  const token = getStoredJwt();
  if (!token) throw new Error("нет токена");
  const res = await fetch(`/api/vaults/${encodeURIComponent(vaultId)}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = (await res.json().catch(() => ({}))) as { error?: string };
  if (!res.ok) throw new Error(data.error ?? `vault delete (${res.status})`);
}

export async function apiFetchVaultMessages(
  vaultId: string,
  limit = 100,
  beforeMs?: number,
  beforeId?: string,
): Promise<{ vault_id: string; messages: VaultServerMessageRow[] }> {
  const token = getStoredJwt();
  if (!token) throw new Error("нет токена");
  const params = new URLSearchParams();
  params.set("limit", String(limit));
  if (beforeMs != null && beforeId != null) {
    params.set("before", String(beforeMs));
    params.set("before_id", beforeId);
  }
  const res = await fetch(`/api/vaults/${encodeURIComponent(vaultId)}/messages?${params}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = (await res.json()) as {
    vault_id?: string;
    messages?: VaultServerMessageRow[];
    error?: string;
  };
  if (!res.ok) throw new Error(data.error ?? `vault messages (${res.status})`);
  return {
    vault_id: data.vault_id ?? vaultId,
    messages: data.messages ?? [],
  };
}

export async function apiFetchVaultMessagesByIds(
  vaultId: string,
  ids: string[],
): Promise<{ vault_id: string; messages: VaultServerMessageRow[] }> {
  if (ids.length === 0) return { vault_id: vaultId, messages: [] };
  const token = getStoredJwt();
  if (!token) throw new Error("нет токена");
  const params = new URLSearchParams();
  params.set("ids", ids.join(","));
  const res = await fetch(`/api/vaults/${encodeURIComponent(vaultId)}/messages?${params}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = (await res.json()) as {
    vault_id?: string;
    messages?: VaultServerMessageRow[];
    error?: string;
  };
  if (!res.ok) throw new Error(data.error ?? `vault messages ids (${res.status})`);
  return {
    vault_id: data.vault_id ?? vaultId,
    messages: data.messages ?? [],
  };
}

export async function apiPostVaultMessage(
  vaultId: string,
  nonceB64: string,
  ciphertextB64: string,
  clientMessageId?: string,
  attachmentBlobIds?: string[],
): Promise<{ id: string; created_at: number }> {
  const token = getStoredJwt();
  if (!token) throw new Error("нет токена");
  const res = await fetch(`/api/vaults/${encodeURIComponent(vaultId)}/messages`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify({
      ...(clientMessageId ? { id: clientMessageId } : {}),
      nonce: nonceB64,
      ciphertext: ciphertextB64,
      ...(attachmentBlobIds && attachmentBlobIds.length > 0 ? { attachment_blob_ids: attachmentBlobIds } : {}),
    }),
  });
  const data = (await res.json()) as { id?: string; created_at?: number; error?: string };
  if (!res.ok || !data.id) throw new Error(data.error ?? `vault post message (${res.status})`);
  return { id: data.id, created_at: data.created_at ?? Date.now() };
}

export async function apiDeleteVaultMessage(vaultId: string, messageId: string): Promise<void> {
  const token = getStoredJwt();
  if (!token) throw new Error("нет токена");
  const res = await fetch(
    `/api/vaults/${encodeURIComponent(vaultId)}/messages/${encodeURIComponent(messageId)}`,
    { method: "DELETE", headers: { Authorization: `Bearer ${token}` } },
  );
  const data = (await res.json().catch(() => ({}))) as { error?: string };
  if (!res.ok) throw new Error(data.error ?? `vault delete message (${res.status})`);
}

export async function apiUploadVaultBlob(
  vaultId: string,
  cipherBytes: Uint8Array,
  onProgress?: (progress: ApiUploadProgress) => void,
): Promise<{ blob_id: string }> {
  const token = getStoredJwt();
  if (!token) throw new Error("нет токена");
  return apiUploadBinary(
    `/api/vault-upload?vault_id=${encodeURIComponent(vaultId)}`,
    token,
    cipherBytes,
    "vault upload",
    onProgress,
  );
}

export function vaultBlobDownloadUrl(blobId: string): string {
  return `/api/vault-upload/${encodeURIComponent(blobId)}`;
}

export async function apiFetchVaultBlob(blobId: string): Promise<ArrayBuffer> {
  const token = getStoredJwt();
  if (!token) throw new Error("нет токена");
  const res = await fetch(vaultBlobDownloadUrl(blobId), {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error ?? `vault blob (${res.status})`);
  }
  return res.arrayBuffer();
}

// ── Pins (закрепы) ──────────────────────────────────────────────

export type PinEntityType = "workspace" | "vault" | "contact";

export type PinItem = {
  entity_type: PinEntityType;
  entity_id: string;
  pinned_at: number;
};

export async function apiFetchPins(): Promise<PinItem[]> {
  const token = getStoredJwt();
  if (!token) throw new Error("нет токена");
  const res = await fetch("/api/pins", {
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = (await res.json()) as { pins?: PinItem[]; error?: string };
  if (!res.ok) throw new Error(data.error ?? `pins (${res.status})`);
  return data.pins ?? [];
}

export async function apiPinEntity(entityType: PinEntityType, entityId: string): Promise<PinItem> {
  const token = getStoredJwt();
  if (!token) throw new Error("нет токена");
  const res = await fetch("/api/pins", {
    method: "PUT",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify({ entity_type: entityType, entity_id: entityId }),
  });
  const data = (await res.json()) as PinItem & { error?: string };
  if (!res.ok) throw new Error(data.error ?? `pin (${res.status})`);
  return data;
}

export async function apiUnpinEntity(entityType: PinEntityType, entityId: string): Promise<void> {
  const token = getStoredJwt();
  if (!token) throw new Error("нет токена");
  const res = await fetch("/api/pins", {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify({ entity_type: entityType, entity_id: entityId }),
  });
  if (!res.ok) {
    const data = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(data.error ?? `unpin (${res.status})`);
  }
}

/** PUT|DELETE /api/workspaces/:id/pinned-topics — shared topic pin (owner/admin). */
export async function apiPinWorkspaceTopic(workspaceId: string, topicId: string): Promise<{
  topic_id: string;
  pinned_by: string;
  pinned_at: number;
}> {
  const token = getStoredJwt();
  if (!token) throw new Error("нет токена");
  const res = await fetch(
    `/api/workspaces/${encodeURIComponent(workspaceId)}/pinned-topics`,
    {
      method: "PUT",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ topic_id: topicId }),
    },
  );
  const data = (await res.json()) as {
    topic_id?: string;
    pinned_by?: string;
    pinned_at?: number;
    error?: string;
  };
  if (!res.ok) throw new Error(data.error ?? `pin topic (${res.status})`);
  return {
    topic_id: data.topic_id ?? topicId,
    pinned_by: data.pinned_by ?? "",
    pinned_at: typeof data.pinned_at === "number" ? data.pinned_at : Date.now(),
  };
}

export async function apiUnpinWorkspaceTopic(workspaceId: string, topicId: string): Promise<void> {
  const token = getStoredJwt();
  if (!token) throw new Error("нет токена");
  const res = await fetch(
    `/api/workspaces/${encodeURIComponent(workspaceId)}/pinned-topics`,
    {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ topic_id: topicId }),
    },
  );
  if (!res.ok) {
    const data = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(data.error ?? `unpin topic (${res.status})`);
  }
}

export type PinnedMessageContextType = "workspace_topic" | "dm" | "vault";

export type PinnedMessageListItem = {
  message_id: string;
  pinned_by: string;
  pinned_at: number;
  created_at: number | null;
  body_preview: string | null;
  author_username: string | null;
  from_id: string | null;
};

export async function apiFetchPinnedMessages(
  contextType: PinnedMessageContextType,
  contextId: string,
): Promise<PinnedMessageListItem[]> {
  const token = getStoredJwt();
  if (!token) throw new Error("нет токена");
  const params = new URLSearchParams({ context_type: contextType, context_id: contextId });
  const res = await fetch(`/api/pinned-messages?${params}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = (await res.json()) as { pins?: PinnedMessageListItem[]; error?: string };
  if (!res.ok) throw new Error(data.error ?? `pinned messages (${res.status})`);
  return data.pins ?? [];
}

export async function apiPinMessage(
  contextType: PinnedMessageContextType,
  contextId: string,
  messageId: string,
): Promise<{ message_id: string; pinned_by: string; pinned_at: number }> {
  const token = getStoredJwt();
  if (!token) throw new Error("нет токена");
  const res = await fetch("/api/pinned-messages", {
    method: "PUT",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify({ context_type: contextType, context_id: contextId, message_id: messageId }),
  });
  const data = (await res.json()) as {
    pin?: { message_id: string; pinned_by: string; pinned_at: number };
    error?: string;
  };
  if (!res.ok) throw new Error(data.error ?? `pin message (${res.status})`);
  const pin = data.pin;
  if (!pin) throw new Error("pin: empty");
  return pin;
}

export async function apiUnpinMessage(
  contextType: PinnedMessageContextType,
  contextId: string,
  messageId: string,
): Promise<void> {
  const token = getStoredJwt();
  if (!token) throw new Error("нет токена");
  const res = await fetch("/api/pinned-messages", {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify({ context_type: contextType, context_id: contextId, message_id: messageId }),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error ?? `unpin message (${res.status})`);
  }
}

export function buildPinnedMessageContextId(
  conv: { kind: "dm"; peerId: string } | { kind: "workspace-topic"; workspaceId: string; topicId: string } | {
    kind: "vault";
    vaultId: string;
  },
  currentUserId: string,
): { contextType: PinnedMessageContextType; contextId: string } {
  if (conv.kind === "dm") {
    return {
      contextType: "dm",
      contextId: buildDmPinnedContextId(currentUserId, conv.peerId),
    };
  }
  if (conv.kind === "vault") {
    return { contextType: "vault", contextId: `vault:${conv.vaultId}` };
  }
  return {
    contextType: "workspace_topic",
    contextId: `wt:${conv.workspaceId}:${conv.topicId}`,
  };
}

function buildDmPinnedContextId(a: string, b: string): string {
  const [x, y] = a < b ? [a, b] : [b, a];
  return `dm:${x}:${y}`;
}


export async function apiLogin(email: string, password: string): Promise<LoginResponse> {
  const res = await fetch("/api/login", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  const data = (await res.json()) as LoginResponse & { error?: string };
  if (!res.ok) {
    throw new Error(data.error ?? `ошибка входа (${res.status})`);
  }
  return data;
}
